import React from 'react';
import { useSelector } from 'react-redux';
import { Button, Typography, Tooltip, message, Dropdown, Modal, Row, Col } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { TASK_REGISTRY } from '../../constants/taskRegistry';
import { getWorkspaceTaskActionKind } from '../workspace/taskActionHelpers';
import { canOpenWorkspaceTaskModal } from '../workspace/workspaceTaskModalResolve';
import { selectUser } from '../../store/slices/authSlice';
import { useCancelControlTaskMutation, type WorkspaceTask } from '../../store/api/workspaceApi';
import {
  buildScopedTaskPresentation,
  type ScopedTaskPresentationContext,
} from '../../domain/scopedTaskPresentation';
import {
  canRoleExecuteClosingPackageTask,
  isClosingPackageApplicationTask,
} from '../../features/closingPackage/workspace/closingPackageTaskPermissions';
import { ScopedTaskPresentationLines } from './ScopedTaskPresentationLines';

const { Text } = Typography;

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#ff4d4f',
  warning: '#faad14',
  info: '#1890ff'
};

const ROUTE_LOGIST_TASKS = new Set([
  'LOADING_PHOTO',
  'UPLOAD_LOADING_PHOTO',
  'UNLOADING_PHOTO',
  'LOADING_DOCUMENTS_UPLOAD',
  'UPLOAD_LOADING_DOCS',
  'UNLOADING_DOCUMENTS_UPLOAD',
  'UPLOAD_UNLOADING_DOCS',
  'NO_DOCS_UNLOADING',
  'CARRIER_INVOICE_MISSING',
  'INVOICE_EXPECTED',
  'UPD_UPLOAD',
  'UPLOAD_UPD',
]);

/** Подтверждение счёта/УПД — assignedRole logist_accountant; ограничение только для ТН ниже */
const ROUTE_ACCOUNTANT_TASKS = new Set(['TN_ORIGINAL_MISSING', 'UPLOAD_TN_ORIGINAL']);

export interface TaskCardProps {
  id: number;
  taskType: string;
  status: string;
  compact?: boolean;
  dueDate?: string | null;
  routeId?: number | null;
  applicationId?: number | null;
  entityType?: string | null;
  invoiceId?: number | null;
  obligationId?: number | null;
  obligationStage?: string | null;
  routeStatus?: string | null;
  loadingCity?: string | null;
  unloadingCity?: string | null;
  carrierName?: string | null;
  /** Full workspace task for scoped presentation (Phase 1D-C). */
  workspaceTask?: WorkspaceTask | null;
  presentationContext?: ScopedTaskPresentationContext;
  onAction?: (task: WorkspaceTask) => void | Promise<void>;
  actionLoading?: boolean;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  id: _taskId,
  taskType,
  status,
  compact = false,
  dueDate,
  routeId,
  applicationId,
  entityType,
  invoiceId,
  obligationId,
  obligationStage,
  routeStatus,
  loadingCity,
  unloadingCity,
  carrierName,
  workspaceTask,
  presentationContext,
  onAction,
  actionLoading = false
}) => {
  const user = useSelector(selectUser);
  const [cancelControlTask, { isLoading: cancelTaskLoading }] = useCancelControlTaskMutation();
  const roleName = typeof user?.role === 'string' ? user.role : (user?.role as any)?.name ?? '';
  const roleLower = roleName?.toLowerCase() ?? '';
  const isAccountant = roleLower === 'accountant';
  const isAdmin = roleLower === 'admin';

  const meta = TASK_REGISTRY[taskType];
  const hasMeta = !!meta;
  const label = meta?.label ?? 'Неизвестная задача';
  const severity = meta?.severity ?? 'info';
  const buttonLabel = meta?.actionLabel ?? 'Открыть';
  const stripColor = SEVERITY_COLORS[severity] ?? SEVERITY_COLORS.info;
  const actionKind = getWorkspaceTaskActionKind(taskType);
  const normalizedTaskType = String(taskType || '').toUpperCase();

  const isClientPaymentDueRestricted =
    normalizedTaskType === 'CLIENT_PAYMENT_DUE' && !isAccountant && !isAdmin;
  const isFinanceRestricted =
    (normalizedTaskType === 'CREATE_CLIENT_INVOICE' &&
      actionKind === 'openFinance' &&
      !isAccountant &&
      !isAdmin) ||
    isClientPaymentDueRestricted;
  const isRouteTask = String(entityType || '').toUpperCase() === 'ROUTE' || routeId != null;
  const isRouteLogistTaskRestricted =
    isRouteTask && ROUTE_LOGIST_TASKS.has(normalizedTaskType) && !roleLower.includes('logist') && !isAdmin;
  const isRouteAccountantTaskRestricted =
    isRouteTask && ROUTE_ACCOUNTANT_TASKS.has(normalizedTaskType) && !isAccountant && !isAdmin;
  const isClosingPackageTask = isClosingPackageApplicationTask(normalizedTaskType);
  const isClosingPackageRoleRestricted =
    isClosingPackageTask &&
    !canRoleExecuteClosingPackageTask(roleName, normalizedTaskType) &&
    !isAdmin;
  const isRoleRestricted =
    isRouteLogistTaskRestricted ||
    isRouteAccountantTaskRestricted ||
    isClosingPackageRoleRestricted;

  const displayLabel =
    normalizedTaskType === 'CLIENT_PAYMENT_DUE'
      ? String(obligationStage || '').toUpperCase() === 'FINAL'
        ? 'Провести платеж — Доплата'
        : 'Провести платеж — Предоплата'
      : label;

  const workspaceTaskPayload: WorkspaceTask = workspaceTask ?? {
    id: _taskId,
    taskType,
    status,
    dueDate: dueDate ?? null,
    routeId: routeId ?? null,
    applicationId: applicationId ?? null,
    entityType: entityType ?? null,
    invoiceId: invoiceId ?? null,
    obligationId: obligationId ?? null,
    obligationStage: obligationStage ?? null,
    routeStatus: routeStatus ?? null,
    loadingCity: loadingCity ?? null,
    unloadingCity: unloadingCity ?? null,
    carrierName: carrierName ?? null,
  };

  const presentation = buildScopedTaskPresentation(workspaceTaskPayload, {
    context: presentationContext,
    titleOverride: displayLabel,
  });

  const handleAction = () => {
    if (!hasMeta || isFinanceRestricted || isRoleRestricted || !onAction) return;

    void onAction(workspaceTaskPayload);
  };

  const isActionDisabled = () => {
    if (!hasMeta) return true;
    if (isFinanceRestricted) return true;
    if (isRoleRestricted) return true;
    if (!onAction) return true;
    if (!canOpenWorkspaceTaskModal(workspaceTaskPayload)) return true;
    return actionLoading;
  };

  const st = String(status).toUpperCase();
  const taskIsActive = st === 'OPEN' || st === 'OVERDUE';
  const isOverdueLike = st === 'OVERDUE';
  const routeCompleted =
    routeStatus != null && String(routeStatus).toUpperCase() === 'COMPLETED';
  const showNotRequired = taskIsActive && isOverdueLike && routeCompleted;

  const handleCancelNotRequired = () => {
    cancelControlTask(_taskId)
      .unwrap()
      .then(() => message.success('Задача закрыта как «не требуется»'))
      .catch((err: any) => message.error(err?.data?.error || err?.message || 'Не удалось закрыть задачу'));
  };

  const handleOpenNotRequiredConfirm = () => {
    Modal.confirm({
      title: 'Закрыть задачу как «не требуется»?',
      okText: 'Да',
      cancelText: 'Нет',
      okButtonProps: { danger: true, loading: cancelTaskLoading },
      onOk: async () => {
        await handleCancelNotRequired();
      },
    });
  };

  const actionButton = (
    <Button
      size="small"
      onClick={handleAction}
      disabled={isActionDisabled()}
      loading={actionLoading}
      style={{ flexShrink: 0 }}
    >
      {buttonLabel}
    </Button>
  );

  const entityRef = entityType === 'APPLICATION' && applicationId
    ? `Заявка #${applicationId}`
    : routeId
      ? `Маршрут #${routeId}`
      : '—';

  /** Контекст маршрута: достаточно routeId с API (города/перевозчик подставляются при наличии). */
  const hasRouteSnapshot = routeId != null;
  const routeEntityLine = hasRouteSnapshot
    ? `${loadingCity ?? '—'} → ${unloadingCity ?? '—'} · ${carrierName?.trim() || '—'} · #${routeId}`
    : null;
  const entityDesc = routeEntityLine ?? entityRef;

  const dueStr = dueDate ? dayjs(dueDate).format('DD.MM.YYYY') : '—';

  const statusLabel =
    status === 'OVERDUE'
      ? 'Просрочено'
      : status === 'OPEN'
        ? 'Открыта'
        : status === 'CANCELLED'
          ? 'Не требуется'
          : status;

  const cardStyle: React.CSSProperties = {
    marginBottom: compact ? 8 : 2,
    padding: compact ? '8px 10px' : '2px 6px',
    background: 'var(--component-background, #fff)',
    border: '1px solid var(--border-color, #f0f0f0)',
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
    borderLeftColor: stripColor,
    borderRadius: 4
  };

  const metaLine =
    presentation.mode === 'scoped'
      ? `${dueStr} · ${statusLabel}`
      : compact
        ? `${dueStr} · ${statusLabel}`
        : `${entityDesc} · ${dueStr} · ${statusLabel}`;

  if (process.env.NODE_ENV === 'development') {
    console.log('[TaskCard route context]', { routeId, loadingCity, unloadingCity });
  }

  const buttons = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {isRoleRestricted ? null : isFinanceRestricted ? (
        <Tooltip title="Доступно только бухгалтеру">
          <span>{actionButton}</span>
        </Tooltip>
      ) : (
        actionButton
      )}
      {showNotRequired && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'not-required',
                label: 'Не требуется',
                danger: true,
              },
            ],
            onClick: ({ key }) => {
              if (key === 'not-required') handleOpenNotRequiredConfirm();
            },
          }}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            aria-label="Дополнительные действия"
            loading={cancelTaskLoading}
          />
        </Dropdown>
      )}
    </span>
  );

  if (compact) {
    return (
      <Row
        className={`task-card task-card--${severity}`}
        align="middle"
        justify="space-between"
        wrap={false}
        gutter={12}
        style={cardStyle}
      >
        <Col flex="1 1 auto" style={{ minWidth: 0 }}>
          <ScopedTaskPresentationLines presentation={presentation} titleStrong fontSize={13} ellipsisTooltip />
          <Text type="secondary" style={{ display: 'block', fontSize: 11, lineHeight: '18px', marginTop: 2 }}>
            {metaLine}
          </Text>
        </Col>
        <Col flex="none" style={{ whiteSpace: 'nowrap' }}>
          {buttons}
        </Col>
      </Row>
    );
  }

  return (
    <Row
      className={`task-card task-card--${severity}`}
      align="middle"
      justify="space-between"
      wrap={false}
      gutter={8}
      style={cardStyle}
    >
      <Col flex="1 1 auto" style={{ minWidth: 0 }}>
        <ScopedTaskPresentationLines presentation={presentation} titleStrong fontSize={13} />
      </Col>
      <Col flex="0 1 auto" style={{ minWidth: 0, maxWidth: presentation.mode === 'scoped' ? '38%' : '52%' }}>
        <Text
          type="secondary"
          ellipsis
          style={{
            display: 'block',
            fontSize: 11,
            lineHeight: '22px',
            whiteSpace: 'nowrap',
            textAlign: 'center'
          }}
        >
          {metaLine}
        </Text>
      </Col>
      <Col flex="none" style={{ whiteSpace: 'nowrap' }}>
        {buttons}
      </Col>
    </Row>
  );
};

export default TaskCard;

